import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { Shop } from "@/stores/auth";

export interface VendorCoverageNoteProps {
  /** Shops whose Seller Central sales actually feed total sales, total orders and TACoS. */
  covered: readonly Pick<Shop, "shopId" | "shopName">[];
  /** Vendor (1P) shops left out until Vendor Central is connected. */
  vendors: readonly Pick<Shop, "shopId" | "shopName">[];
  className?: string;
}

/**
 * Filter-bar chip for a mixed seller/vendor scope (#679): the SP-API backed
 * numbers only cover the seller shops, and the tooltip names which ones.
 */
export function VendorCoverageNote({
  covered,
  vendors,
  className,
}: VendorCoverageNoteProps): React.ReactElement {
  const { t } = useTranslation("dashboard");
  const names = (shops: readonly Pick<Shop, "shopName">[]) =>
    shops.map((shop) => shop.shopName).join(", ");
  return (
    <Tooltip>
      <TooltipTrigger
        data-slot="vendor-coverage-note"
        className={cn(
          "rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring",
          className,
        )}
        render={<button type="button" />}
      >
        {t("overview.vendorData.coverage", { count: covered.length })}
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        {t("overview.vendorData.coverageHint", {
          covered: names(covered),
          vendors: names(vendors),
        })}
      </TooltipContent>
    </Tooltip>
  );
}
